import classNames from "classnames";
import React, {
    useEffect,
    useMemo,
    useRef, useState,
} from "react";
import ReactQuill from "react-quill";
import { RiBrainLine, RiCheckboxCircleFill, RiGlobalLine } from "@remixicon/react";
import { ArtificialIntelligenceToolInput, useClearAiPromptMutation, useGetAiPromptLazyQuery } from "../../__generated__/api";
import createId from "../../create-id";
import Styles from "./AIQuestion.module.scss";
import Editor from "../agent-v3/editor/Editor";
import Confirm from "../confirm/Confirm";
import { HtmlRenderer, santitize, StripFormatting } from "../html-render/HtmlRenderer";
import Button from "../theme/button/Button";
import Icon from "../theme/icon/Icon";
import Text from "../theme/text/Text";
import Dropdown from "../theme/dropdown/Dropdown";
import Portal from "../portal/Portal";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";
import Toggle from "../theme/toggle/Toggle";

const FORMAT = "\n\nRespond in HTML format and do not include any other markup. ";

export interface AvailableActionParameter {
    name: string;
    description: string;
}
export interface AvailableAction {
    id: string;
    name: string;
    prompt: string;
    parameters: AvailableActionParameter[]
}

export interface AvailableActionResponseAction {
    id: string;
    name: string;
    parameters: Record<string, unknown>
}

export interface AvailableActionResponseButton {
    buttonName: string;
    actions: AvailableActionResponseAction[]
}

interface AvailableActionResponse {
    text: string;
    actionButtons: AvailableActionResponseButton[]
}

interface ResponseState {
    id: string;
    isLoading: boolean;
    question: string;
    answer?: string,
    actions?: AvailableActionResponseButton[]
}
const ACTIONS = `
The response JSON should have the following 2 properties:
- text - The assistant text response encoded as HTML correctly breaking special characters
- actionButtons - are described below:

# actionButtons
As the assistant, you can suggest or recommend actions which will be available to the user to execute. ACTIONS ARE NOT TOOLS, they are only suggestions of actions that the user can take.
These actions should be recommendations to the user but never automatically taking action.
Actions are optional and not every response requires an action button suggestion. They should only be suggested if they
will help the user do their job or its likely that the user will take that action. If no action would be a logical next step,
dont make any recommendations.
More than one action can be suggested.

A actionButtons can be a combination of actions as formatted JSON array. These should be returned in the regular response and these are not a tool call.
An action can be called multiple times with different parameters as part of the same actionButton or as multiple actionButtons.
Each actionButton is a JSON object in the JSON array should represent a button available to the user have and have the following format:
- buttonName: the name you want to give the button. For example, instead of "Change Status", you can say "Change Status to Assigned" but keep the button names relatively short. This should not be HTML but a regular string
- actions: the array of actions to invoke. This array can be a combination of available actions with different parameters that will all invoke at the same time. This array should be in the following format:
   - id: the provided id of the action
   - parameters: a single JSON object with the parameters of the action. The parameters are the ones provided in the prompt.

For example, if the available actions are called "close-ticket", "add-note", you can create a button that calls them both at the same time by returning action buttons that look like this:
{
    "buttonName": "Close ticket and add notes",
    "actions": [
        {
            "id": "close-ticket",
            "parameters": {}
        },
        {
            "id": "add-note",
            "parameters": {
                "note": "This is a first note"
            }
        },
        {
            "id": "add-note",
            "parameters": {
                "note": "This is a second note"
            }
        }
    ]
}

## Available Actions Are:
{{ACTIONS}}
`;

const SCHEMA: ArtificialIntelligenceToolInput = {
    properties: {
        text: {
            type: "string",
            description: "The assistant text response encoded as HTML correctly breaking special characters",
        },
        actionButtons: {
            type: "array",
            description: "Optional array of suggested user actions that can be triggered via buttons",
            items: {
                type: "object",
                required: ["buttonName", "actions"],
                properties: {
                    buttonName: {
                        type: "string",
                        description: "A short, user-friendly name for the button. Not HTML, plain string.",
                    },
                    actions: {
                        type: "array",
                        description: "Array of actions to be invoked when the button is clicked. "
                            + "All actions will execute together.",
                        items: {
                            type: "object",
                            required: ["id", "parameters"],
                            properties: {
                                id: {
                                    type: "string",
                                    description: "The unique identifier of the action to invoke",
                                },
                                parameters: {
                                    type: "object",
                                    description: "A key-value map of parameters required by the action",
                                    additionalProperties: true,
                                },
                            },
                        },
                    },
                },
            },
        },
    },
    required: ["text"],
    isAllowAdditionalProperties: false,
};

const SYSTEM_ACTIONS: AvailableAction[] = [
    {
        id: "next-step",
        name: "Next Search",
        prompt: "Offers the user to take a next step and search for something further with AI. This action will allow the user to invoke the next AI query.",
        parameters: [
            {
                name: "query",
                description: "The next query that when clicked, will be send back to AI.",
            },
        ],
    },
    {
        id: "close-ai",
        name: "Close AI",
        prompt: "Closes the AI window so that the user can get back to editing the ticket and no longer talking to AI. "
            + "Often you want to suggest this action to allow the user to get back to handling the ticket.",
        parameters: [
        ],
    },
];

const CONTEXT = "You must respond with valid JSON only. No other text, explanations, or markdown formatting "
+ "should be included in your response. \n\n"
+ "This is a ticketing system for short form communications. Depending on the source type, "
+ "any proposed responses should be short.";
function parseActions(text: string) {
    try {
        const json = JSON.parse(text) as AvailableActionResponse;

        return {
            text: json.text,
            actions: json.actionButtons,
        };
    } catch (err) {
        //
    }

    return {
        text,
        actions: [],
    };
}
export default function AIQuestion({
    chatId, onHide, availableActions, invokeAction, additionalContext,
    placeholder, shouldShow, metricPathPrefix, className, optionsClassName, shouldUseTools,
}: {
    chatId?: string
    onHide: () => void;
    availableActions: AvailableAction[];
    invokeAction: (action: AvailableActionResponseAction) => void;
    additionalContext?: string;
    placeholder?: string;
    shouldShow?: boolean;
    metricPathPrefix: string[] | undefined;
    className?: string;
    optionsClassName?: string;
    shouldUseTools?: boolean;
}) {
    const [promptText, setPromptText] = useState<string | undefined>("");
    const [responses, setResponses] = useState<ResponseState[]>([]);
    const [isAllowExternal, setAllowExternal] = useState<boolean>(true);
    const [isAllowWebSearch, setAllowWebSearch] = useState<boolean>(false);

    const [getPrompt] = useGetAiPromptLazyQuery();
    const [clearContext] = useClearAiPromptMutation();

    const quillRef = useRef<ReactQuill>(null);
    const inputRef = useRef<HTMLDivElement>(null);

    const actionContext = useMemo(() => ACTIONS.replace("{{ACTIONS}}", JSON.stringify(
        [...availableActions, ...SYSTEM_ACTIONS],
    )), [availableActions]);

    LoggingHandler.instance.useMetricWithCondition(
        [...(metricPathPrefix ?? []), "ask-ai", "open"],
        () => shouldShow === true && metricPathPrefix != null,
        [shouldShow],
    );
    useEffect(() => {
        quillRef.current?.focus();
        inputRef.current?.scrollIntoView();
    }, [responses, shouldShow]);

    const sendPrompt = async (currentMessage?: string) => {
        // eslint-disable-next-line no-param-reassign
        currentMessage ||= promptText || currentMessage;
        const plainText = currentMessage ? santitize(currentMessage, { stripFormatting: StripFormatting.ALL }) : "";
        const newResponse: ResponseState = {
            id: createId(),
            isLoading: true,
            question: plainText?.capitalize() as string,
            answer: "",
        };
        const requestContext = responses.map(
            ({ question, answer }) => `\nQuestion: ${question}\nAnswer: ${answer}`,
        ).join("");

        setResponses(current => [
            ...current,
            newResponse,
        ]);
        setPromptText("");

        const response = await getPrompt({
            variables: {
                options: {
                    chatId,
                    prompt: plainText as string + FORMAT,
                    input: `${CONTEXT}\n${
                        !isAllowExternal
                            ? `Do not rely on any external knowledge or make assumptions. If the answer is not present`
                            + ` in the context, respond with: "I'm sorry, I don't have that information." Do not refer`
                            + ` to anything you know as "the knowledge provided"`
                            : ""
                    }\n${
                        additionalContext
                    }\n\nPrevious Questions and AI generated answers from the assistant:\n${
                        requestContext
                    }\n\n${
                        actionContext
                    }`,
                    schema: SCHEMA,
                    isWebSearchEnabled: isAllowWebSearch,
                    isEnabledTools: shouldUseTools,
                },
            },
        });
        newResponse.isLoading = false;
        const { text, actions } = parseActions(response.data?.artificialIntelligencePrompt?.response as string);
        newResponse.answer = text;
        newResponse.actions = actions;
        setResponses(current => [
            ...current,
        ]);
    };

    const sendPromptByUser = metricPathPrefix ? LoggingHandler.instance.wrapMetric(
        [...metricPathPrefix, "ask-ai", "question"],
        sendPrompt,
    ) : sendPrompt;

    const handleHide = metricPathPrefix ? LoggingHandler.instance.wrapMetric(
        [...metricPathPrefix, "ask-ai", "close"],
        onHide,
    ) : onHide;

    const invokeActionStep = (action: AvailableActionResponseAction) => {
        if (action.id === "next-step") {
            sendPrompt(action.parameters.query as string);
        } else if (action.id === "close-ai") {
            handleHide();
        } else {
            invokeAction(action);
        }
    };

    const clear = async () => {
        setResponses([]);
        quillRef.current?.focus();
        if (chatId) {
            await clearContext({
                variables: {
                    chatId: chatId as string,
                },
            });
        }
    };

    return (
        <Confirm
            title="Ask the AI"
            shouldShow={shouldShow ?? true}
            size="lg"
            className={className}
            shouldShowFooter={false}
            onHide={() => handleHide()}
            bodyClassName={classNames(
                Styles["ai-question-body"],
                responses.isEmpty ? Styles["ai-question-body-empty"] : undefined,
            )}
        >
            <div className={Styles["ai-question-response-list"]}>
                {responses.map(response => (
                    <div key={response.id} className={Styles["ai-question-responses"]}>

                        <Text
                            as="div"
                            textVariant="body-2-regular"
                            colorVariant="text-link"
                            className={Styles["ai-question-text-box"]}
                        >
                            <Icon.ArtificialIntelligence size="xsmall" />
                            <HtmlRenderer html={response.question} shouldTrimBottom />
                        </Text>

                        {response.isLoading ? (
                            <div className={classNames("loading", Styles["ai-loading"], Styles["ai-question-box"])}>
                                <div className="loading">
                                    Loading text commands...
                                </div>
                            </div>
                        ) : (
                            <Text as="div" textVariant="body-1-regular" className={Styles["ai-question-box"]}>
                                <HtmlRenderer html={response.answer ?? ""} />
                                {response.actions?.isNotEmpty && (
                                    <div className={Styles["ai-question-actions"]}>
                                        {response.actions?.map(action => (
                                            <Button.ArtificialIntelligence
                                                onClick={() => action.actions.forEach(child => invokeActionStep(child))}
                                                metricPath={metricPathPrefix
                                                    ? [...metricPathPrefix, "ask-ai", "action"]
                                                    : undefined}
                                                variant="outline-blue"
                                                size="xsmall"
                                            >
                                                {action.buttonName}
                                            </Button.ArtificialIntelligence>
                                        ))}
                                    </div>
                                )}
                            </Text>
                        )}

                    </div>
                ))}
                <div ref={inputRef} />
            </div>
            <Editor
                value={promptText as string}
                onSend={sendPromptByUser}
                onChange={value => setPromptText(value)}
                canFormat={false}
                ref={quillRef}
                rootClassName={Styles["ai-question-entry-box"]}
                placeholder={placeholder}
                sendButton={(
                    <div className={Styles["ai-question-entry-buttons"]}>
                        <Dropdown drop="up">
                            <Dropdown.Toggle prefixIcon={<Icon.More />} size="small" variant="ghost-gray" />
                            <Portal>
                                <Dropdown.Menu className={classNames(Styles["ai-question-entry-button-menu"], optionsClassName)}>
                                    <Dropdown.Item
                                        prefixIcon={<Icon.Delete />}
                                        onClick={clear}
                                    >
                                        Clear
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        prefixIcon={<Icon icon={<RiGlobalLine />} />}
                                        onClick={() => setAllowWebSearch(!isAllowWebSearch)}
                                    >
                                        <div className={Styles["ai-text-option"]}>
                                            <div>
                                                Web Search
                                            </div>

                                            <Toggle
                                                size="small"
                                                checked={isAllowWebSearch}
                                                onChange={() => {
                                                    setAllowWebSearch(!isAllowWebSearch);
                                                }}
                                            />
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        prefixIcon={<Icon icon={<RiBrainLine />} />}
                                        onClick={() => setAllowExternal(!isAllowExternal)}
                                    >
                                        <div className={Styles["ai-text-option"]}>
                                            <div>
                                                Allow External Knoweldge
                                            </div>

                                            <Toggle
                                                size="small"
                                                checked={isAllowExternal}
                                                onChange={() => {
                                                    setAllowExternal(!isAllowExternal);
                                                }}
                                            />
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Portal>

                        </Dropdown>
                        <Button
                            size="small"
                            variant="primary"
                            type="submit"
                            onClick={evt => {
                                evt.preventDefault();
                                sendPromptByUser();
                            }}
                            prefixIcon={<Icon.ArtificialIntelligence size="xsmall" />}
                        >
                            Ask AI
                        </Button>
                    </div>
                )}
            />
        </Confirm>
    );
}

AIQuestion.defaultProps = {
    chatId: undefined,
    additionalContext: undefined,
    placeholder: "I have a question regarding...",
    shouldShow: true,
    className: undefined,
    optionsClassName: undefined,
    shouldUseTools: undefined,

};
